import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle2, Download, FileText, PlayCircle, RotateCcw, VideoOff } from 'lucide-react';
import SimuladorBonificacao from './simulador/SimuladorBonificacao';
import { AULAS_PARCEIRO, BUCKET_ACADEMY, MATERIAIS_PARCEIRO, MODULOS_PARCEIRO } from './cursoParceiro';
import type { Aula } from './cursoParceiro';
import { assinarArquivos, carregarConcluidas, desmarcarConcluida, marcarConcluida } from './progressoAcademy';

// Vigor Academy — o Minicurso do Parceiro: 7 aulas em vídeo (sem música, com
// legenda na imagem), o resumo em texto de cada uma, os PDFs e a calculadora.
// A aula aberta vai na URL (?aula=...) para dar para mandar o link de uma aula
// específica pelo WhatsApp.

const CHAVE_ULTIMA_AULA = 'academy:ultima-aula';

function lerUltimaAula(): string | null {
    try {
        return localStorage.getItem(CHAVE_ULTIMA_AULA);
    } catch {
        return null;
    }
}

function guardarUltimaAula(id: string) {
    try {
        localStorage.setItem(CHAVE_ULTIMA_AULA, id);
    } catch {
        /* navegação privada: só não lembra a última aula */
    }
}

const totalAulas = AULAS_PARCEIRO.length;
const moduloDa = (aula: Aula) => MODULOS_PARCEIRO.find(m => m.aulas.some(a => a.id === aula.id))!;

const ItemAula: React.FC<{ aula: Aula; atual: boolean; concluida: boolean; onAbrir: (id: string) => void }> = ({ aula, atual, concluida, onAbrir }) => (
    <li>
        <button
            type="button"
            onClick={() => onAbrir(aula.id)}
            aria-current={atual ? 'true' : undefined}
            className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                atual ? 'bg-primary/10 dark:bg-primary/25' : 'hover:bg-gray-50 dark:hover:bg-gray-700/60'
            }`}
        >
            <span
                className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold tabular-nums ${
                    concluida
                        ? 'bg-primary text-white'
                        : atual
                            ? 'border-2 border-primary text-primary dark:border-primary-light dark:text-primary-light'
                            : 'border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                }`}
                aria-hidden="true"
            >
                {concluida ? <Check size={14} strokeWidth={3} /> : aula.numero}
            </span>
            <span className="min-w-0 flex-1">
                <span className={`block text-sm leading-snug ${atual ? 'font-semibold text-gray-900 dark:text-white' : 'font-medium text-gray-700 dark:text-gray-200'}`}>
                    {aula.titulo}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    {aula.duracao} min{concluida ? ' · concluída' : ''}
                </span>
            </span>
            {atual ? <PlayCircle size={18} className="shrink-0 text-primary dark:text-primary-light" aria-hidden="true" /> : null}
        </button>
    </li>
);

const AcademyView: React.FC = () => {
    const [params, setParams] = useSearchParams();
    const [concluidas, setConcluidas] = useState<Set<string>>(() => new Set());
    const [urls, setUrls] = useState<Record<string, string> | null>(null);
    const [terminou, setTerminou] = useState(false);

    // Aula aberta: a do link; senão a última vista; senão a primeira.
    const idPedido = params.get('aula');
    const aula = useMemo(
        () => AULAS_PARCEIRO.find(a => a.id === idPedido) ?? AULAS_PARCEIRO.find(a => a.id === lerUltimaAula()) ?? AULAS_PARCEIRO[0],
        [idPedido],
    );
    const indice = AULAS_PARCEIRO.indexOf(aula);
    const proxima = AULAS_PARCEIRO[indice + 1] ?? null;
    const modulo = moduloDa(aula);
    const concluida = concluidas.has(aula.id);

    useEffect(() => {
        let ativo = true;
        const caminhos = [...AULAS_PARCEIRO.flatMap(a => [a.video, a.capa]), ...MATERIAIS_PARCEIRO.map(m => m.arquivo)];
        Promise.all([carregarConcluidas(), assinarArquivos(BUCKET_ACADEMY, caminhos)]).then(([feitas, assinadas]) => {
            if (!ativo) return;
            setConcluidas(feitas);
            setUrls(assinadas);
        });
        return () => {
            ativo = false;
        };
    }, []);

    useEffect(() => {
        guardarUltimaAula(aula.id);
        setTerminou(false);
    }, [aula.id]);

    const abrir = useCallback(
        (id: string) => {
            setParams({ aula: id }, { replace: false });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [setParams],
    );

    const concluir = useCallback((id: string) => {
        setConcluidas(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
        marcarConcluida(id);
    }, []);

    const desfazer = useCallback((id: string) => {
        setConcluidas(prev => {
            const nova = new Set(prev);
            nova.delete(id);
            return nova;
        });
        desmarcarConcluida(id);
    }, []);

    const feitas = AULAS_PARCEIRO.filter(a => concluidas.has(a.id)).length;
    const urlVideo = urls?.[aula.video];

    return (
        // O layout do app só ganha margem lateral a partir do sm; no celular, a margem é desta tela.
        <div className="space-y-6 px-4 sm:px-0">
            <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div className="max-w-3xl">
                    <h1 className="text-2xl font-bold font-poppins text-gray-900 dark:text-white">Vigor Academy</h1>
                    <p className="text-gray-600 dark:text-gray-300 mt-1">
                        Minicurso do Parceiro: como usar o VigorHub e calcular a sua bonificação, em {totalAulas} aulas curtas.
                    </p>
                </div>
                <div className="md:w-64 shrink-0">
                    <p className="text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                        {feitas === totalAulas ? (
                            <span className="font-semibold text-primary dark:text-primary-light">Minicurso concluído</span>
                        ) : (
                            <>
                                <span className="font-semibold text-gray-900 dark:text-white">{feitas} de {totalAulas}</span> aulas concluídas
                            </>
                        )}
                    </p>
                    <div className="mt-2 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-primary origin-left transition-transform duration-300 ease-out"
                            style={{ transform: `scaleX(${feitas / totalAulas})` }}
                        />
                    </div>
                </div>
            </header>

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
                <div className="space-y-6 min-w-0">
                    <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-gray-950 shadow-sm">
                        {urls === null ? (
                            <div className="absolute inset-0 animate-pulse bg-gray-800" aria-label="Carregando o vídeo" />
                        ) : urlVideo ? (
                            <video
                                key={aula.id}
                                src={urlVideo}
                                poster={urls[aula.capa]}
                                controls
                                playsInline
                                preload="metadata"
                                onEnded={() => {
                                    concluir(aula.id);
                                    setTerminou(true);
                                }}
                                onPlay={() => setTerminou(false)}
                                className="absolute inset-0 h-full w-full"
                            >
                                Seu navegador não toca este vídeo.
                            </video>
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center text-gray-300">
                                <VideoOff size={32} className="text-gray-500" aria-hidden="true" />
                                <p className="font-semibold">Este vídeo ainda não foi publicado.</p>
                                <p className="text-sm text-gray-400">O resumo da aula está logo abaixo.</p>
                            </div>
                        )}

                        {terminou ? (
                            <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 bg-gradient-to-t from-black/85 via-black/60 to-transparent px-5 pb-16 pt-12 sm:flex-row sm:items-center sm:justify-between motion-safe:animate-fade-in">
                                <p className="flex items-center gap-2 font-semibold text-white">
                                    <CheckCircle2 size={20} className="text-secondary" aria-hidden="true" />
                                    {proxima ? 'Aula concluída' : 'Você concluiu o minicurso'}
                                </p>
                                {proxima ? (
                                    <button
                                        type="button"
                                        onClick={() => abrir(proxima.id)}
                                        className="inline-flex items-center gap-2 self-start rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition-transform duration-150 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                                    >
                                        Próxima: {proxima.titulo} <ArrowRight size={16} aria-hidden="true" />
                                    </button>
                                ) : (
                                    <a
                                        href="#calculadora"
                                        onClick={e => {
                                            e.preventDefault();
                                            document.getElementById('calculadora')?.scrollIntoView({ behavior: 'smooth' });
                                        }}
                                        className="inline-flex items-center gap-2 self-start rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                                    >
                                        Abrir a calculadora <ArrowRight size={16} aria-hidden="true" />
                                    </a>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                            <p className="text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                                {modulo.titulo} · Aula {aula.numero} de {totalAulas} · {aula.duracao} min
                            </p>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mt-1">{aula.titulo}</h2>
                            <p className="text-gray-600 dark:text-gray-300 mt-1 max-w-[65ch]">{aula.descricao}</p>
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2">
                            {/* Um botão de alternância só: marcado = aula concluída (clicar de novo desmarca). */}
                            <button
                                type="button"
                                aria-pressed={concluida}
                                onClick={() => (concluida ? desfazer(aula.id) : concluir(aula.id))}
                                title={concluida ? 'Desmarcar esta aula' : undefined}
                                className="inline-flex items-center gap-2 rounded-full border border-gray-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 transition-[background-color,transform] duration-150 ease-out hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            >
                                {concluida ? (
                                    <>
                                        <CheckCircle2 size={16} className="text-primary dark:text-primary-light" aria-hidden="true" />
                                        Aula concluída
                                        <RotateCcw size={14} className="text-gray-400" aria-hidden="true" />
                                    </>
                                ) : (
                                    <>
                                        <Check size={16} aria-hidden="true" />
                                        Marcar como concluída
                                    </>
                                )}
                            </button>
                            {proxima ? (
                                <button
                                    type="button"
                                    onClick={() => abrir(proxima.id)}
                                    className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-primary-dark active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
                                >
                                    Próxima aula <ArrowRight size={16} aria-hidden="true" />
                                </button>
                            ) : null}
                        </div>
                    </div>

                    <section aria-labelledby="titulo-resumo" className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 sm:p-6">
                        <h3 id="titulo-resumo" className="text-base font-bold text-gray-900 dark:text-white">O que você aprende nesta aula</h3>
                        <ul className="mt-3 space-y-2.5">
                            {aula.pontos.map(p => (
                                <li key={p} className="flex gap-3 text-sm leading-relaxed text-gray-700 dark:text-gray-300 max-w-[70ch]">
                                    <Check size={16} className="shrink-0 mt-0.5 text-primary dark:text-primary-light" aria-hidden="true" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </section>
                </div>

                <aside className="space-y-6 lg:sticky lg:top-24">
                    <nav aria-label="Aulas do minicurso" className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
                        {MODULOS_PARCEIRO.map(m => (
                            <div key={m.id} className="py-1">
                                <h3 className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">{m.titulo}</h3>
                                <ol>
                                    {m.aulas.map(a => (
                                        <ItemAula key={a.id} aula={a} atual={a.id === aula.id} concluida={concluidas.has(a.id)} onAbrir={abrir} />
                                    ))}
                                </ol>
                            </div>
                        ))}
                    </nav>

                    <section aria-labelledby="titulo-materiais" className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
                        <h3 id="titulo-materiais" className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">Materiais para baixar</h3>
                        <ul>
                            {MATERIAIS_PARCEIRO.map(m => {
                                const url = urls?.[m.arquivo];
                                return (
                                    <li key={m.id}>
                                        {url ? (
                                            <a
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                            >
                                                <FileText size={18} className="shrink-0 text-secondary-dark dark:text-secondary" aria-hidden="true" />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{m.titulo}</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400">{m.descricao}</span>
                                                </span>
                                                <Download size={16} className="shrink-0 text-gray-400" aria-hidden="true" />
                                            </a>
                                        ) : (
                                            <div className="flex items-center gap-3 rounded-xl px-3 py-2.5 opacity-60">
                                                <FileText size={18} className="shrink-0 text-gray-400" aria-hidden="true" />
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">{m.titulo}</span>
                                                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                                                        {urls === null ? 'Carregando…' : 'Ainda não publicado.'}
                                                    </span>
                                                </span>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                            <li>
                                <a
                                    href="#calculadora"
                                    onClick={e => {
                                        e.preventDefault();
                                        document.getElementById('calculadora')?.scrollIntoView({ behavior: 'smooth' });
                                    }}
                                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                >
                                    <ArrowRight size={18} className="shrink-0 text-primary dark:text-primary-light" aria-hidden="true" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">Simulador de bonificação</span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400">Some os consumidores do mês e veja quanto recebe.</span>
                                    </span>
                                </a>
                            </li>
                        </ul>
                    </section>
                </aside>
            </div>

            <div id="calculadora" className="scroll-mt-24">
                <SimuladorBonificacao />
            </div>
        </div>
    );
};

export default AcademyView;
